/**
 * Record Payment Modal Component
 * Form to record a payment for an invoice
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PaymentMethod, RecordPaymentRequest } from '@/types';
import { format } from 'date-fns';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payment: RecordPaymentRequest) => void;
  isSubmitting?: boolean;
  error?: string | null;
  maxAmount: number;
  invoiceNumber: string;
}

export const RecordPaymentModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error = null,
  maxAmount,
  invoiceNumber,
}: RecordPaymentModalProps) => {
  const [formData, setFormData] = useState<RecordPaymentRequest>({
    amount: maxAmount,
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    transactionReference: '',
    notes: '',
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.amount <= 0) {
      setValidationError('Amount must be greater than 0');
      return;
    }

    if (formData.amount > maxAmount) {
      setValidationError(`Amount cannot exceed balance due (₹${maxAmount.toFixed(2)})`);
      return;
    }

    setValidationError(null);
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      amount: maxAmount,
      paymentMethod: PaymentMethod.BANK_TRANSFER,
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      transactionReference: '',
      notes: '',
    });
    setValidationError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record a payment for invoice {invoiceNumber}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Error Alert */}
            {(error || validationError) && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error || validationError}</AlertDescription>
              </Alert>
            )}

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount <span className="text-red-500">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                max={maxAmount}
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: parseFloat(e.target.value) })
                }
                placeholder="Enter amount"
                required
              />
              <p className="text-sm text-muted-foreground">
                Balance due: ₹{maxAmount.toFixed(2)}
              </p>
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label htmlFor="paymentDate">
                Payment Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="paymentDate"
                type="date"
                value={formData.paymentDate}
                onChange={(e) =>
                  setFormData({ ...formData, paymentDate: e.target.value })
                }
                max={format(new Date(), 'yyyy-MM-dd')}
                required
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">
                Payment Method <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.paymentMethod}
                onValueChange={(value) =>
                  setFormData({ ...formData, paymentMethod: value as PaymentMethod })
                }
              >
                <SelectTrigger id="paymentMethod">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PaymentMethod.CASH}>Cash</SelectItem>
                  <SelectItem value={PaymentMethod.BANK_TRANSFER}>
                    Bank Transfer
                  </SelectItem>
                  <SelectItem value={PaymentMethod.UPI}>UPI</SelectItem>
                  <SelectItem value={PaymentMethod.CHEQUE}>Cheque</SelectItem>
                  <SelectItem value={PaymentMethod.CARD}>Card</SelectItem>
                  <SelectItem value={PaymentMethod.WALLET}>Wallet</SelectItem>
                  <SelectItem value={PaymentMethod.OTHER}>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Transaction Reference */}
            <div className="space-y-2">
              <Label htmlFor="transactionReference">Transaction Reference</Label>
              <Input
                id="transactionReference"
                value={formData.transactionReference}
                onChange={(e) =>
                  setFormData({ ...formData, transactionReference: e.target.value })
                }
                placeholder="e.g., Cheque number, UPI ID, Transaction ID"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Additional notes (optional)"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recording...
                </>
              ) : (
                'Record Payment'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
